"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import BookingTable from "./common/Table";
import {
  useAddEntranceTicket,
  useGetAllEntranceTickets,
  useUpdateEntranceTicket,
  useDeleteEntranceTicket,
} from "@/hooks/useEntrance";
import {
  X,
  Trash2,
  Edit,
  UploadIcon,
  DownloadCloudIcon,
  TrashIcon,
  Plus,
  Search,
  ChevronDown,
} from "lucide-react";
import Button from "./Masters/Button";
import { toast } from "react-toastify";
import { useGetCityActivityList, useGetSightSeeingNameList } from "@/hooks/useCommon";
import { useDebounce } from "@/hooks/useDebounce";
import { useGetNationality } from "@/hooks/useCommon"; 

// Updated Zod validation schema - removed status field
const entranceTicketSchema = z.object({
  activity_id: z.string().min(1, "Sightseeing is required"),
  nationality: z.string().optional(),
  type: z.string().min(1, "Ticket type is required"),
  adult_fees: z.string().min(1, "Adult fees is required").regex(/^\d+(\.\d{1,2})?$/, "Must be a valid number"),
  youth_fees: z.string().optional().refine(val => !val || /^\d+(\.\d{1,2})?$/.test(val), {
    message: "Must be a valid number"
  }),
  senior_citizen_fees: z.string().optional().refine(val => !val || /^\d+(\.\d{1,2})?$/.test(val), {
    message: "Must be a valid number"
  }),
  child_fees: z.string().optional().refine(val => !val || /^\d+(\.\d{1,2})?$/.test(val), {
    message: "Must be a valid number"
  }),
});

type EntranceTicketFormData = z.infer<typeof entranceTicketSchema>;

interface TableData {
  id: number;
  sno: number;
  activity_id: number;
  nationality: number;
  type: string;
  adult_fees: string;
  youth_fees: string;
  senior_citizen_fees: string;
  child_fees: string;
  status: number;
  created_by: string;
  created_at: string;
  sightseeing_name?: string;
  nationality_id: number;
  nationalities?: {
    id: number,
    name: string;
    nationality: string;
  }
}

const EntranceTicketMaster = () => {
  // Search filter states
  const [ticketType, setTicketType] = useState("");
  const [sightseeingId, setSightseeingId] = useState("");
  const [nationality, setNationality] = useState("");
  const [nationalitySearch, setNationalitySearch] = useState("");
  const [showNationalityDropdown, setShowNationalityDropdown] = useState(false);
  const [selectedNationalityLabel, setSelectedNationalityLabel] = useState("");
  const [activeStatus, setActiveStatus] = useState(true);
  const [inactiveStatus, setInactiveStatus] = useState(false);
  
  // Modal states
  const [currentRow, setCurrentRow] = useState<TableData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Refs
  const nationalityDropdownRef = useRef<HTMLDivElement>(null);
  const nationalityInputRef = useRef<HTMLInputElement>(null);

  // Data fetching
  const { data: ticketData, refetch: refetchTickets } = useGetAllEntranceTickets();
  const [rawTicketData, setRawTicketData] = useState<any[]>([]);
  const [filteredTicketData, setFilteredTicketData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  
  // Mutations
  const { mutate: addTicketMutate } = useAddEntranceTicket();
  const { mutate: updateTicketMutate } = useUpdateEntranceTicket();
  const { mutate: deleteTicketMutate } = useDeleteEntranceTicket();

  // Hooks for dropdowns
  const { data: sightseeingData, isLoading: isLoadingSightseeing } = useGetSightSeeingNameList();
  const { data: cityActivityData, isLoading: isLoadingCityActivity } = useGetCityActivityList();
  
  // Use debounce for nationality search
  const debouncedNationalitySearch = useDebounce(nationalitySearch, 300);
  
  // Use separate nationality hook
  const { data: nationalityData, isLoading: isLoadingNationality } = useGetNationality(debouncedNationalitySearch);

  // Form setup - removed status from default values
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<EntranceTicketFormData>({
    resolver: zodResolver(entranceTicketSchema),
    defaultValues: {
      activity_id: "",
      nationality: "",
      type: "",
      adult_fees: "",
      youth_fees: "",
      senior_citizen_fees: "",
      child_fees:"",
    }
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (nationalityDropdownRef.current && 
          !nationalityDropdownRef.current.contains(event.target as Node)) {
        setShowNationalityDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-open dropdown when typing
  useEffect(() => {
    if (debouncedNationalitySearch.length >= 3 && nationalityData && nationalityData.length > 0) {
      setShowNationalityDropdown(true);
    } else {
      setShowNationalityDropdown(false);
    }
  }, [debouncedNationalitySearch, nationalityData]);

  // Update selected nationality label when value changes
  useEffect(() => {
    if (nationality && nationalityData) {
      const selected = nationalityData.find((item: any) => item.id?.toString() === nationality);
      if (selected) {
        setSelectedNationalityLabel(`${selected.name} (${selected.code})`);
        setNationalitySearch(selected.name);
      }
    }
  }, [nationality, nationalityData]);

  // Refetch tickets
  const refetchEntranceTickets = () => {
    refetchTickets();
  };

  useEffect(() => {
    refetchEntranceTickets();
  }, []);

  useEffect(() => {
    if (Array.isArray(ticketData?.data)) {
      setRawTicketData(ticketData.data);
      setFilteredTicketData(ticketData.data);
    } else if (Array.isArray(ticketData)) {
      setRawTicketData(ticketData);
      setFilteredTicketData(ticketData);
    }
  }, [ticketData]);




  // Format table data
  const formattedTicketData = useMemo(() => {
    if (!Array.isArray(filteredTicketData)) return [];
    
    return filteredTicketData.map((item: any, index: number) => ({
      sno: index + 1,
      id: item.id,
      activity_id: item.activity_id,
      sightseeing_name: item.masterActivity.title || `Sightseeing #${item.activity_id}`,
      nationality: item.nationalities?.nationality || item.nationalities?.name || "Unknown",
      nationality_id: item?.nationalities?.id,
      nationalities: item.nationalities,
      type: item.type || "General",
      adult_fees: item.adult_fees ? `$${parseFloat(item.adult_fees).toFixed(2)}` : "-",
      youth_fees: item.youth_fees ? `$${parseFloat(item.youth_fees).toFixed(2)}` : "-",
      senior_citizen_fees: item.senior_citizen_fees ? `$${parseFloat(item.senior_citizen_fees).toFixed(2)}` : "-",
      child_fees: item.child_fees ? `$${parseFloat(item.child_fees).toFixed(2)}` : "-",
      status: item.status === 1 ? "Active" : "Inactive",
      created_by: item.creator.first_name || "-",
      created_at: new Date(item.created_date).toLocaleDateString(),
    }));
  }, [filteredTicketData]);

  // Sightseeing options
  // const sightseeingOptions = useMemo(() => {
  //   if (!sightseeingData?.data) return [];
  //   return sightseeingData.data.map((item: any) => ({
  //     value: item.s_id?.toString() || item.id?.toString(),
  //     label: item.name || item.sightseeing_name || "Unknown Sightseeing"
  //   }));
  // }, [sightseeingData]);

    // Sightseeing options
  const cityActivityOptions = useMemo(() => {
    if (!cityActivityData?.data) return [];
    return cityActivityData.data.map((item: any) => ({
      value: item.s_id?.toString() || item.id?.toString(),
      label: item.name || item.title || "Unknown activity"
    }));
  }, [cityActivityData]);

  // Nationality options from API
  const nationalityOptions = useMemo(() => {
    if (!nationalityData) return [];
    return nationalityData.map((item: any) => ({
      id: item.id?.toString(),
      name: item.name,
      code: item.code,
      label: `${item.name} (${item.code})`
    }));
  }, [nationalityData]);

  // Handle nationality selection
  const handleNationalitySelect = (nationalityId: string, nationalityName: string, nationalityCode: string) => {
    setNationality(nationalityId);
    setSelectedNationalityLabel(`${nationalityName} (${nationalityCode})`);
    setNationalitySearch(nationalityName);
    setValue("nationality", nationalityId);
    setShowNationalityDropdown(false);
  };

  // Clear nationality
  const handleClearNationality = () => {
    setNationality("");
    setSelectedNationalityLabel("");
    setNationalitySearch("");
    setValue("nationality", "");
    setShowNationalityDropdown(false);
  };

  // Search handler
  const handleSearch = () => {
    const filtered = rawTicketData.filter((item) => {
      // Ticket type filter
      const matchType = ticketType 
        ? item.type?.toLowerCase().includes(ticketType.toLowerCase())
        : true;

      // Sightseeing filter
      const matchSightseeing = sightseeingId 
        ? item.activity_id?.toString() === sightseeingId
        : true;

      // Nationality filter
      const matchNationality = nationality 
        ? item.nationality?.toString() === nationality
        : true;

      // Status filter
      const matchStatus =
        activeStatus && inactiveStatus
          ? true
          : activeStatus
          ? item.status == 1
          : inactiveStatus
          ? item.status == 0
          : true;

      return matchType && matchSightseeing && matchNationality && matchStatus;
    });

    setFilteredTicketData(filtered);
  };

  // Clear search filters
  const handleClear = () => {
    setTicketType("");
    setSightseeingId("");
    handleClearNationality();
    setActiveStatus(true);
    setInactiveStatus(false);
    setFilteredTicketData(rawTicketData);
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isAddModalOpen) {
      reset({
        activity_id: "",
        nationality: "",
        type: "",
        adult_fees: "",
        youth_fees: "",
        senior_citizen_fees: "",
        child_fees: "",
      });
      setNationalitySearch("");
      setSelectedNationalityLabel("");
    }
  }, [isAddModalOpen, reset]);

  useEffect(() => {
    if (isModalOpen && currentRow) {
      
      reset({
        activity_id: currentRow.activity_id?.toString() || "",
        nationality: currentRow.nationality_id?.toString() || "",
        type: currentRow.type || "",
        adult_fees: currentRow.adult_fees?.replace('$', '') || "",
        youth_fees: currentRow.youth_fees?.replace('$', '') || "",
        senior_citizen_fees: currentRow.senior_citizen_fees?.replace('$', '') || "",
        child_fees: currentRow.child_fees?.replace('$', '') || "",
      });
      
      // Update nationality search state
      if (currentRow.nationality) {
        // You might need to fetch the nationality name here
        setNationality(currentRow.nationality_id?.toString());
        setNationalitySearch(currentRow?.nationalities?.name || "")
        setSelectedNationalityLabel(
          `${currentRow?.nationalities?.name} (${currentRow?.nationalities?.nationality})`
        );
      }
    }
  }, [isModalOpen, currentRow, reset]);

  // Form submission handler - status is always 1 by default
  const onSubmit = (formData: EntranceTicketFormData) => {
    const payload = {
      activity_id: parseInt(formData.activity_id),
      nationality: formData.nationality ? parseInt(formData.nationality) : null,
      type: formData.type,
      adult_fees: formData.adult_fees,
      youth_fees: formData.youth_fees || null,
      senior_citizen_fees: formData.senior_citizen_fees || null,
      child_fees: formData.child_fees || null,
      status: 1, 
      created_by: 1,
      modified_by: 1,
      ip: "127.0.0.1"
    };

    if (currentRow) {
      // Update existing ticket
      payload.id = currentRow.id;
      
      updateTicketMutate(payload, {
        onSuccess: (res) => {
          toast.success("Ticket updated successfully");
          refetchEntranceTickets();
          setIsModalOpen(false);
          setCurrentRow(null);
          reset();
          setNationalitySearch("");
          setSelectedNationalityLabel("");
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || "Failed to update ticket";
          toast.error(errorMessage);
        },
      });
    } else {
      // Add new ticket
      addTicketMutate(payload, {
        onSuccess: (res) => {
          toast.success("Ticket added successfully");
          refetchEntranceTickets();
          setIsAddModalOpen(false);
          reset();
          setNationalitySearch("");
          setSelectedNationalityLabel("");
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || "Failed to add ticket";
          toast.error(errorMessage);
        },
      });
    }
  };

  // Status change handler
  const handleStatusChange = (status_id: number, id?: any) => {
    let ticket_ids = [id];
    
    if (!id) {
      if (selectedRowIds.length === 0) {
        toast.error("No rows selected");
        return;
      }
      ticket_ids = selectedRowIds;
    }

    if (status_id === 0) { // Soft delete (status = 0)
      deleteTicketMutate(
        {
          ids: ticket_ids,
        },
        {
          onSuccess: (data) => {
            toast.success("Ticket(s) deleted successfully");
            refetchEntranceTickets();
            setSelectedRowIds([]);
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || "Failed to delete ticket(s)";
            toast.error(errorMessage);
          },
        }
      );
    } else {
      // For update status (activate/deactivate)
      const updatePayload = {
        id: ticket_ids[0],
        status: status_id,
        modified_by: 1,
        ip: "127.0.0.1"
      };
      
      updateTicketMutate(
        updatePayload,
        {
          onSuccess: (data) => {
            toast.success("Status updated successfully");
            refetchEntranceTickets();
            setSelectedRowIds([]);
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || "Failed to update status";
            toast.error(errorMessage);
          },
        }
      );
    }
  };

  // Row selection handlers
  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allIds = filteredTicketData.map((row) => row.id);
      setSelectedRowIds(allIds);
    } else {
      setSelectedRowIds([]);
    }
  };

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedRowIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  const allRowsSelected =
    filteredTicketData?.length > 0 &&
    selectedRowIds?.length === filteredTicketData?.length;

  // Table columns
  const columns = [
    {
      key: "checkbox",
      header: (
        <input
          type="checkbox"
          onChange={(e) => handleSelectAll(e.target.checked)}
          checked={allRowsSelected}
        />
      ),
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRowIds.includes(row.id)}
          onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "sightseeing_name", header: "Sightseeing", sortable: true },
    { key: "type", header: "Ticket Type", sortable: true },
    { key: "nationality", header: "Nationality", sortable: true },
    { key: "adult_fees", header: "Adult Fees", sortable: true },
    { key: "youth_fees", header: "Youth Fees", sortable: true },
    {key: "child_fees", header: "Child Fees", sortable: true},
    { key: "senior_citizen_fees", header: "Senior Fees", sortable: true },
    { 
      key: "status", 
      header: "Status", 
      sortable: true,
      render: (status: string) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          status === "Active" 
            ? "bg-green-100 text-green-800" 
            : "bg-red-100 text-red-800"
        }`}>
          {status}
        </span>
      )
    },
    { key: "created_by", header: "Created By", sortable: true },
    { key: "created_at", header: "Created Date", sortable: true },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentRow(row);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => handleStatusChange(0, row.id)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Ticket type options
  const ticketTypeOptions = [
    { label: "General", value: "General" },
    { label: "VIP", value: "VIP" },
    { label: "Premium", value: "Premium" },
    { label: "Family", value: "Family" },
    { label: "Group", value: "Group" },
    { label: "Student", value: "Student" },
    { label: "Senior Citizen", value: "Senior Citizen" },
  ];

  return (
    <div className="w-full bg-white">
      <div className="flex flex-wrap items-center gap-6 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
        {/* Ticket Type Search */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Ticket Type</label>
          <input
            type="text"
            value={ticketType}
            onChange={(e) => setTicketType(e.target.value)}
            placeholder="Search by ticket type"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
          />
        </div>

        {/* Sightseeing Selection */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Sightseeing</label>
          <select
            value={sightseeingId}
            onChange={(e) => setSightseeingId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            disabled={isLoadingSightseeing}
          >
            <option value="">All Sightseeing</option>
            {cityActivityOptions.map((option:any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Nationality Search */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Nationality</label>
          <div className="relative" ref={nationalityDropdownRef}>
            <div className="relative">
              <input
                type="text"
                value={nationalitySearch}
                onChange={(e) => {
                  setNationalitySearch(e.target.value);
                  setNationality("");
                  setSelectedNationalityLabel("");
                  if (e.target.value.length >= 3) {
                    setShowNationalityDropdown(true);
                  }
                }}
                placeholder="Type at least 3 characters..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px] pr-10"
                ref={nationalityInputRef}
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                {nationality && (
                  <button
                    type="button"
                    onClick={handleClearNationality}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )}
                <Search className="h-4 w-4 text-gray-400" />
              </div>
            </div>
            
            {/* Nationality Dropdown */}
            {showNationalityDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {isLoadingNationality ? (
                  <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                ) : nationalityOptions.length > 0 ? (
                  nationalityOptions.map((option:any) => (
                    <div
                      key={option.id}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${
                        nationality === option.id ? "bg-blue-50 text-blue-700" : "text-gray-700"
                      }`}
                      onClick={() => handleNationalitySelect(option.id, option.name, option.code)}
                    >
                      {option.label}
                    </div>
                  ))
                ) : debouncedNationalitySearch.length >= 3 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">Type at least 3 characters...</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Status */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <div className="flex items-center gap-4">
            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="active"
                  checked={activeStatus}
                  onChange={(e) => setActiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setActiveStatus(!activeStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    activeStatus ? "bg-green-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      activeStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="active"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Active
              </label>
            </div>

            {/* Inactive Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="inactive"
                  checked={inactiveStatus}
                  onChange={(e) => setInactiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setInactiveStatus(!inactiveStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    inactiveStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      inactiveStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="inactive"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Inactive
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 ml-auto">
          <Button
            size="sm"
            onClick={handleSearch}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Search
          </Button>

          <Button
            size="sm"
            onClick={handleClear}
            variant="secondary"
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Ticket
          </Button>

          <Button
            size="sm"
            onClick={() => handleStatusChange(1)}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Activate
          </Button>

          <Button
            size="sm"
            onClick={() => handleStatusChange(0)}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Deactivate
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={() => handleStatusChange(0)}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Delete
          </Button>
        </div>
        <BookingTable
          data={formattedTicketData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={false}
          sortable={true}
          itemsPerPage={10}
        />
      </div>

      {/* Update Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentRow(null);
              reset();
              setNationalitySearch("");
              setSelectedNationalityLabel("");
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Update Entrance Ticket
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentRow(null);
                    reset();
                    setNationalitySearch("");
                    setSelectedNationalityLabel("");
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {/* Sightseeing Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sightseeing *
                    </label>
                    <select
                      {...register("activity_id")}
                      className={`w-full border ${errors.activity_id ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white`}
                      disabled={isLoadingCityActivity}
                    >
                      <option value="">Select Sightseeing</option>
                      {cityActivityOptions.map((option:any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.activity_id && (
                      <p className="text-red-500 text-xs mt-1">{errors.activity_id.message}</p>
                    )}
                  </div>

                  {/* Ticket Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ticket Type *
                    </label>
                    <select
                      {...register("type")}
                      className={`w-full border ${errors.type ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white`}
                    >
                      <option value="">Select Ticket Type</option>
                      {ticketTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.type && (
                      <p className="text-red-500 text-xs mt-1">{errors.type.message}</p>
                    )}
                  </div>

                  {/* Nationality */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nationality (Optional)
                    </label>
                    <div className="relative" ref={nationalityDropdownRef}>
                      <div className="relative">
                        <input
                          type="text"
                          value={nationalitySearch}
                          onChange={(e) => {
                            setNationalitySearch(e.target.value);
                            setNationality("");
                            setValue("nationality", "");
                            if (e.target.value.length >= 3) {
                              setShowNationalityDropdown(true);
                            }
                          }}
                          placeholder="Type at least 3 characters..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                          {nationality && (
                            <button
                              type="button"
                              onClick={handleClearNationality}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X size={14} />
                            </button>
                          )}
                          <Search className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Nationality Dropdown */}
                      {showNationalityDropdown && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {isLoadingNationality ? (
                            <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                          ) : nationalityOptions.length > 0 ? (
                            nationalityOptions.map((option:any) => (
                              <div
                                key={option.id}
                                className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${
                                  nationality === option.id ? "bg-blue-50 text-blue-700" : "text-gray-700"
                                }`}
                                onClick={() => {
                                  handleNationalitySelect(option.id, option.name, option.code);
                                  setValue("nationality", option.id);
                                }}
                              >
                                {option.label}
                              </div>
                            ))
                          ) : debouncedNationalitySearch.length >= 3 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500">Type at least 3 characters...</div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Hidden input for form submission */}
                    <input type="hidden" {...register("nationality")} />
                  </div>

                  {/* Fees Section */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Adult Fees */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adult Fees
                      </label>
                      <input
                        type="text"
                        {...register("adult_fees")}
                        placeholder="0.00"
                        className={`w-full border ${errors.adult_fees ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.adult_fees && (
                        <p className="text-red-500 text-xs mt-1">{errors.adult_fees.message}</p>
                      )}
                    </div>

                    {/* Youth Fees */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Youth Fees
                      </label>
                      <input
                        type="text"
                        {...register("youth_fees")}
                        placeholder="0.00"
                        className={`w-full border ${errors.youth_fees ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.youth_fees && (
                        <p className="text-red-500 text-xs mt-1">{errors.youth_fees.message}</p>
                      )}
                    </div>


                    {/* child fees  */}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Child Fees
                      </label>
                      <input
                        type="text"
                        {...register("child_fees")}
                        placeholder="0.00"
                        className={`w-full border ${errors.child_fees ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.child_fees && (
                        <p className="text-red-500 text-xs mt-1">{errors.child_fees.message}</p>
                      )}
                    </div>

                    {/* Senior Citizen Fees */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Senior Citizen Fees
                      </label>
                      <input
                        type="text"
                        {...register("senior_citizen_fees")}
                        placeholder="0.00"
                        className={`w-full border ${errors.senior_citizen_fees ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.senior_citizen_fees && (
                        <p className="text-red-500 text-xs mt-1">{errors.senior_citizen_fees.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setIsModalOpen(false);
                        setCurrentRow(null);
                        reset();
                        setNationalitySearch("");
                        setSelectedNationalityLabel("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">
                      Update Ticket
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsAddModalOpen(false);
              reset();
              setNationalitySearch("");
              setSelectedNationalityLabel("");
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Add New Entrance Ticket
                </h2>
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                    reset();
                    setNationalitySearch("");
                    setSelectedNationalityLabel("");
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {/* Sightseeing Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sightseeing *
                    </label>
                    <select
                      {...register("activity_id")}
                      className={`w-full border ${errors.activity_id ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white`}
                      disabled={isLoadingCityActivity}
                    >
                      <option value="">Select Sightseeing</option>
                      {cityActivityOptions.map((option:any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.activity_id && (
                      <p className="text-red-500 text-xs mt-1">{errors.activity_id.message}</p>
                    )}
                  </div>

                  {/* Ticket Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ticket Type *
                    </label>
                    <select
                      {...register("type")}
                      className={`w-full border ${errors.type ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white`}
                    >
                      <option value="">Select Ticket Type</option>
                      {ticketTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.type && (
                      <p className="text-red-500 text-xs mt-1">{errors.type.message}</p>
                    )}
                  </div>

                  {/* Nationality */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nationality (Optional)
                    </label>
                    <div className="relative" ref={nationalityDropdownRef}>
                      <div className="relative">
                        <input
                          type="text"
                          value={nationalitySearch}
                          onChange={(e) => {
                            setNationalitySearch(e.target.value);
                            setNationality("");
                            setValue("nationality", "");
                            if (e.target.value.length >= 3) {
                              setShowNationalityDropdown(true);
                            }
                          }}
                          placeholder="Type at least 3 characters..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                          {nationality && (
                            <button
                              type="button"
                              onClick={handleClearNationality}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X size={14} />
                            </button>
                          )}
                          <Search className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Nationality Dropdown */}
                      {showNationalityDropdown && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {isLoadingNationality ? (
                            <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                          ) : nationalityOptions.length > 0 ? (
                            nationalityOptions.map((option:any) => (
                              <div
                                key={option.id}
                                className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${
                                  nationality === option.id ? "bg-blue-50 text-blue-700" : "text-gray-700"
                                }`}
                                onClick={() => {
                                  handleNationalitySelect(option.id, option.name, option.code);
                                  setValue("nationality", option.id);
                                }}
                              >
                                {option.label}
                              </div>
                            ))
                          ) : debouncedNationalitySearch.length >= 3 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500">Type at least 3 characters...</div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Hidden input for form submission */}
                    <input type="hidden" {...register("nationality")} />
                  </div>

                  {/* Fees Section */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Adult Fees */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adult Fees
                      </label>
                      <input
                        type="text"
                        {...register("adult_fees")}
                        placeholder="0.00"
                        className={`w-full border ${errors.adult_fees ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.adult_fees && (
                        <p className="text-red-500 text-xs mt-1">{errors.adult_fees.message}</p>
                      )}
                    </div>

                    {/* Youth Fees */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Youth Fees
                      </label>
                      <input
                        type="text"
                        {...register("youth_fees")}
                        placeholder="0.00"
                        className={`w-full border ${errors.youth_fees ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.youth_fees && (
                        <p className="text-red-500 text-xs mt-1">{errors.youth_fees.message}</p>
                      )}
                    </div>


                    {/* child fees  */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Child Fees
                      </label>
                      <input
                        type="text"
                        {...register("child_fees")}
                        placeholder="0.00"
                        className={`w-full border ${errors.child_fees ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.child_fees && (
                        <p className="text-red-500 text-xs mt-1">{errors.child_fees.message}</p>
                      )}
                    </div>

                    {/* Senior Citizen Fees */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Senior Citizen Fees
                      </label>
                      <input
                        type="text"
                        {...register("senior_citizen_fees")}
                        placeholder="0.00"
                        className={`w-full border ${errors.senior_citizen_fees ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.senior_citizen_fees && (
                        <p className="text-red-500 text-xs mt-1">{errors.senior_citizen_fees.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setIsAddModalOpen(false);
                        reset();
                        setNationalitySearch("");
                        setSelectedNationalityLabel("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">
                      Add Ticket
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntranceTicketMaster;